import { client } from "@l2r-front/l2r-query"

import { useRoadworksQuery } from "../../../../common/hooks/queries/useRoadworksQuery"
import { constructTechniqueAcronym, sortTasks } from "../../utils/roadworkUtils"

import { roadworksQueryKeys } from "./queryKeys"
import { useTasks } from "./useTask"
import { useTaskTypes } from "./useTaskType"

function initialData(
    road,
    uuid,
) {
    const queries = client.getQueriesData({ queryKey: roadworksQueryKeys.list({ linear_location_road: road }) })
    for (const [_, queryData] of queries) {
        if (!queryData) {
            continue
        }
        const roadwork = queryData.find((result) => {
            return result.uuid === uuid
        })

        if (roadwork) {
            return roadwork
        }
    }

    return undefined
}

export function useRoadwork(
    roadworkUuid,
    road,
    config = {},
    params = {},
) {
    const url = `roadworks/${roadworkUuid}/`
    const key = roadworksQueryKeys["roadwork"](roadworkUuid, params)
    const {
        autoRefetch = false,
        enabled = true,
        ...queryConfig
    } = config

    const queryInitialData = params["as_geojson"]
        ? undefined
        : initialData(road, roadworkUuid)

    const {
        data: roadwork,
        isLoading: isLoadingRoadworks,
        isError: isErrorRoadworks,
    } = useRoadworksQuery(
        url,
        key,
        params,
        {
            ...queryConfig,
            addProjectParam: false,
            autoRefetch,
            enabled: !!roadworkUuid && enabled,
        },
        queryInitialData,
    )

    const {
        data: taskTypes,
        isLoading: isLoadingTaskTypes,
        isError: isErrorTaskTypes,
    } = useTaskTypes()

    const isLoading = isLoadingRoadworks || isLoadingTaskTypes
    const isError = isErrorRoadworks || isErrorTaskTypes

    if (params["as_geojson"]) {
        return { data: roadwork, isLoading, isError }
    }

    const data = roadwork ? {
        ...roadwork,
        technique: {
            ...roadwork?.technique,
            acronym: constructTechniqueAcronym(roadwork.technique, taskTypes),
        },
    } : null

    return { data, isLoading, isError }
}

export function useRoadworkWithTasks(
    roadworkUuid,
    road,
    config = {},
) {

    const { enabled = true } = config

    const {
        data: roadworkData,
        isLoading: roadworkLoading,
        isError: roadworkError,
    } = useRoadwork(roadworkUuid, road, config)

    const {
        data: tasksData,
        isError: tasksError,
        isLoading: tasksLoading,
    } = useTasks(roadworkData?.tasks, { enabled: !!roadworkData?.tasks && enabled })

    const {
        data: taskTypes,
        isLoading: taskTypesLoading,
        isError: taskTypesError,
    } = useTaskTypes({ enabled: enabled })

    const tasks = sortTasks(tasksData, taskTypes)

    const isLoading = roadworkLoading || tasksLoading || taskTypesLoading
    const isError = roadworkError || tasksError || taskTypesError
    const data = (roadworkData && tasksData) ? { ...roadworkData, tasks: tasks }
        : null

    return { data, isLoading, isError }
}
